import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { contributionsApi } from "@/api/contributions.api";
import type { HistoryFilters, HistoryStatus } from "./contribution-history";
import { HISTORY_PAGE_SIZE, historyParams, toHistoryEntry } from "./history-query";

const STATUSES: Array<HistoryStatus | "all"> = ["all", "validated", "pending", "rejected"];

/**
 * One page of the contributor's history from the server, plus the count
 * behind each status chip (same language and dates, one-row requests).
 */
export function useContributionHistory(userId: string | undefined, filters: HistoryFilters, page: number) {
  const pageQuery = useQuery({
    queryKey: ["my-contributions", "history", userId, filters, page],
    queryFn: () => contributionsApi.getUserHistory(userId!, historyParams(filters, page)),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });

  const countQueries = useQueries({
    queries: STATUSES.map((status) => ({
      queryKey: ["my-contributions", "history-count", userId, filters.language, filters.from, filters.to, status],
      queryFn: () => contributionsApi.getUserHistory(userId!, historyParams({ ...filters, status }, 1, 1)),
      enabled: Boolean(userId),
      staleTime: 30_000,
    })),
  });

  const counts = Object.fromEntries(
    STATUSES.map((status, i) => [status, countQueries[i].data?.pagination.total ?? 0]),
  ) as Record<HistoryStatus | "all", number>;

  const total = pageQuery.data?.pagination.total ?? 0;
  return {
    entries: (pageQuery.data?.data ?? []).map(toHistoryEntry),
    total,
    pageCount: Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE)),
    counts,
    isLoading: pageQuery.isLoading,
    isError: pageQuery.isError,
    isFetching: pageQuery.isFetching,
    refetch: pageQuery.refetch,
  };
}
